import {animate, style, transition, trigger} from '@angular/animations';
import {AsyncPipe} from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {ActivatedRoute, NavigationEnd, Router, RouterOutlet} from '@angular/router';
import {select, Store} from '@ngrx/store';
import {BehaviorSubject, Subscription} from 'rxjs';
import {filter, take} from 'rxjs/operators';
import {Project} from '../core/model/project';
import {TitleService} from '../core/page/title.service';
import {GetLocationsAction} from '../core/store/locations/locations.action';
import {GetProgramsAction} from '../core/store/programs/programs.action';
import {GetProjectsAction} from '../core/store/projects/projects.action';
import {selectProjectsMap} from '../core/store/projects/projects.selector';
import {GetCoinTypesAction} from '../core/store/rewards/rewards.action';
import {FetchSkillsAction} from '../core/store/skills/skills.action';
import {GetTeamsAction} from '../core/store/teams/teams.action';
import {GetAllUsersAction} from '../core/store/users/users.action';
import {MessageService} from '../services/message.service';
import {CreateFirstModule} from '../shared/design/create-first/create-first.module';
import {OphLoadingModule} from '../shared/design/oph-loading/oph-loading.module';
import {PipesModule} from '../shared/pipes/pipes.module';
import {ProjectsDialogComponent} from './dialogs/projects-dialog/projects-dialog.component';
import {PROJECTS_DIALOG_CONFIG} from './shared/projects-constants';
import {MobileService} from 'src/app/core/page/mobile.service';

@Component({
  selector: 'projects',
  standalone: true,
  imports: [OphLoadingModule, CreateFirstModule, PipesModule, RouterOutlet, AsyncPipe],
  templateUrl: './projects.component.html',
  styleUrl: './projects.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('swipe', [
      transition(':increment', [
        style({transform: 'translateX(100%)'}),
        animate('350ms ease-out', style({transform: 'translateX(0)'})),
      ]),
      transition(':decrement', [
        style({transform: 'translateX(-100%)'}),
        animate('350ms ease-out', style({transform: 'translateX(0)'})),
      ]),
    ]),
  ],
})
export class ProjectsComponent implements OnInit, OnDestroy {
  subscriptions = new Subscription();

  @HostBinding('class.swipe-animating') isAnimating = false;
  /** 0 = list, 1 = project/challenge detail, 2 = challenge-builder. Drives swipe animation. */
  viewIndex = new BehaviorSubject(0);

  loading$ = new BehaviorSubject<boolean>(true);
  noProjects$ = new BehaviorSubject<boolean>(false);

  private readonly mobileService = inject(MobileService);
  readonly router = inject(Router);
  readonly dialog = inject(MatDialog);
  readonly route = inject(ActivatedRoute);
  readonly messageService = inject(MessageService);
  readonly cdr = inject(ChangeDetectorRef);

  constructor(
    private store$: Store,
    private titleService: TitleService
  ) {}

  ngOnInit() {
    this.titleService.setPageTitle('Projects');

    this.store$.dispatch(new GetProgramsAction({}));
    this.store$.dispatch(new GetAllUsersAction({}));
    this.store$.dispatch(new FetchSkillsAction({}));
    this.store$.dispatch(new GetLocationsAction({}));
    this.store$.dispatch(new GetTeamsAction({}));
    this.store$.dispatch(new GetCoinTypesAction({}));

    this.store$.dispatch(
      new GetProjectsAction({
        onSuccess: () => this.loading$.next(false),
        onFailure: () => this.loading$.next(false),
      })
    );

    this.subscriptions.add(this.subscribeToRedirectAfterLoad());
    this.subscriptions.add(this.subscribeToRouteForSwipe());
  }

  private subscribeToRouteForSwipe(): Subscription {
    const setViewIndex = (url: string) => {
      if (url.startsWith('/projects/challenge-builder') || /^\/projects\/[^/]+\/edit$/.test(url)) {
        this.viewIndex.next(2);
      } else {
        const onDetail =
          /^\/projects\/[^/]+/.test(url) && !url.startsWith('/projects/build') && !url.startsWith('/projects/view');
        this.viewIndex.next(onDetail ? 1 : 0);
      }
    };
    setViewIndex(this.router.url);
    return this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe(e => {
      setViewIndex(e.urlAfterRedirects);
    });
  }

  onSwipeStart(): void {
    this.isAnimating = true;
    this.cdr.markForCheck();
  }

  onSwipeDone(): void {
    this.isAnimating = false;
    this.cdr.markForCheck();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  /** When loading becomes false, run redirect logic: list vs stored project, or invalid projectId. */
  private subscribeToRedirectAfterLoad(): Subscription {
    return this.loading$
      .pipe(
        filter(loading => !loading),
        take(1)
      )
      .subscribe(() => {
        this.store$.pipe(select(selectProjectsMap), take(1)).subscribe(projectsMap => {
          const hasProjects = projectsMap && Object.keys(projectsMap).length > 0;
          if (!hasProjects) {
            this.noProjects$.next(true);
            return;
          }
          this.noProjects$.next(false);

          const projectId = this.route.firstChild?.snapshot?.paramMap?.get('projectId');

          if (projectId) {
            // We're on /projects/:projectId — validate project exists
            if (!projectsMap[projectId]) {
              sessionStorage.removeItem('ophStoredProjectId');
              this.messageService.add(
                'That project no longer exists or you don’t have access. You’ve been taken back to the list.',
                'error'
              );
              this.router.navigate(['/projects'], {replaceUrl: true});
            }
          } else {
            // We're on /projects (list) — only redirect if no explicit route was saved
            // (i.e. the user hasn't deliberately navigated back to the list)
          }
        });
      });
  }

  onOpenProjectDialog(currentProject?: Project) {
    const dialog = this.dialog.open(ProjectsDialogComponent, {
      autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
      ...PROJECTS_DIALOG_CONFIG,
      data: {project: currentProject},
    });
    dialog.afterClosed().subscribe(project => {
      if (project) {
        this.onActionSuccess(project);
      }
    });
  }

  onActionSuccess(project: Project) {
    this.store$.dispatch(new GetProjectsAction({}));
    if (project) {
      sessionStorage.setItem('ophStoredProjectId', project._id);
      this.router.navigate([project._id], {relativeTo: this.route});
    }
  }
}
